/**
 * Sprint 12+M5.20 (27.07.2026) — MusterCard (vormals EndgegnerModusCard).
 *
 * Prominente CTA-Card im Pionier-Cockpit, die auf die Vimeo-
 * Indoctrination-Sequenz (/dashboard/pionier/videos) fuehrt.
 *
 * Copy (Andreas-Freigabe, "Endgegner"-Sprache raus, "Muster kennenlernen" in):
 *  - Kicker:    "DEIN MUSTER"
 *  - Headline:  "Dein Einstieg in die Welt der Fokustypen"
 *  - Subline:   "Wer steuert deine Reaktionen, wenn's kippt?"
 *  - Badge:     "{unlockedCount} von 5 freigeschaltet · Dein Muster kennenlernen"
 *  - CTA:       "▶ Jetzt kennenlernen →"
 *
 * Drip-Logik: unlockOffsetDays aus VIMEO_VIDEO_ORDER, gerechnet
 * ab user.created_at (Fallback user.pionier_since, sonst 1 Video).
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { VIMEO_VIDEO_ORDER, vimeoThumbnailUrl, VIMEO_VIDEOS } from "@/config/vimeo";
import { Play, ArrowRight } from "lucide-react";

function computeUnlockedCount(user) {
  // Sprint M5.48 Hotfix (02.08.2026): Der Staffel-Anker ist der
  // Pionier-Signup, nicht der User-Account-Anfang. Sonst wuerden User,
  // die nachtraeglich Pionier werden, sofort alles freigeschaltet sehen.
  const signup = user?.pionier_since || user?.created_at || null;
  if (!signup) return 2; // Intro + Primaerfokustyp sind immer Tag-0.

  const signupDate = new Date(signup);
  if (Number.isNaN(signupDate.getTime())) return 2;

  const now = new Date();
  const hoursSince = (now.getTime() - signupDate.getTime()) / (1000 * 60 * 60);

  // Sprint M5.48 Hotfix (02.08.2026): Das Fokustyp-Video, das dem
  // Primaerfokustyp des Users entspricht, ist immer sofort freigeschaltet
  // (Offset 0) — unabhaengig von seiner statischen Offset-Angabe in der Config.
  const primary = (user?.primary_fokustyp || user?.fokustyp_primary || "").toLowerCase();

  const unlocked = VIMEO_VIDEO_ORDER.filter((v) => {
    const effectiveOffset = v.key === primary ? 0 : (v.unlockOffsetDays || 0);
    const hours = effectiveOffset * 24;
    return hoursSince >= hours;
  }).length;

  return Math.max(2, unlocked); // mind. Intro + Primaerfokustyp
}

export default function MusterCard({ user }) {
  const navigate = useNavigate();

  const unlockedCount = useMemo(() => computeUnlockedCount(user), [user]);
  const totalCount = VIMEO_VIDEO_ORDER.length;
  const allUnlocked = unlockedCount >= totalCount;

  const thumbnailUrl =
    vimeoThumbnailUrl(VIMEO_VIDEOS.intro) ||
    vimeoThumbnailUrl(VIMEO_VIDEO_ORDER[0]);

  const handleClick = () => {
    // Sprint 12+M5.20 — Meta-Tracking bei Card-Klick.
    try {
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("trackCustom", "MusterCardClick", {
          card_type: "muster_feature",
          user_fokustyp: user?.primary_fokustyp || user?.fokustyp_primary || null,
          unlocked_count: unlockedCount,
        });
      }
      if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: "muster_card_click",
          card_type: "muster_feature",
          user_fokustyp: user?.primary_fokustyp || user?.fokustyp_primary || null,
          unlocked_count: unlockedCount,
        });
      }
    } catch (_err) {
      // Tracking-Fehler brechen den Klick nicht ab.
    }
    navigate("/dashboard/pionier/videos");
  };

  const badgeText = allUnlocked
    ? "Alle 5 freigeschaltet · Jetzt ansehen"
    : `${unlockedCount} von ${totalCount} freigeschaltet · Jetzt ansehen`;

  return (
    <div
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
      data-testid="muster-card"
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-orange-200 shadow-md shadow-orange-500/10 transition-all duration-300 hover:scale-[1.01] hover:shadow-lg hover:shadow-orange-500/20 focus:outline-none focus:ring-2 focus:ring-[#f97316] focus:ring-offset-2"
      style={{
        background:
          "linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fde3c7 100%)",
      }}
    >
      {/* Sprint M5.24.5 (24.07.2026) — Watermark-Grid der 4 Fokus-Typ-Icons
          gibt der Card Kontext, ohne die Kern-CTA zu überlagern.
          Sprint M5.49 Hotfix (02.08.2026) — Matrix-Reihenfolge korrigiert.
          Verbindlich: Katze OL, Eule OR, Hund UL, Biene UR. In einem
          2-Spalten-Grid heisst das Array-Reihenfolge [katze, eule, hund, biene]. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 grid grid-cols-2 gap-2 sm:gap-3 opacity-[0.14]"
        data-testid="muster-card-watermark"
      >
        {["katze", "eule", "hund", "biene"].map((slug) => (
          <img
            key={slug}
            src={`/icons/icon-${slug}.png`}
            alt=""
            className="w-10 h-10 sm:w-14 sm:h-14 object-contain"
            draggable="false"
          />
        ))}
      </div>

      <div className="relative flex flex-col sm:flex-row gap-5 p-5 sm:p-6">
        {/* Thumbnail */}
        <div className="flex-shrink-0 relative w-full sm:w-[140px] h-[100px] sm:h-[90px] rounded-xl overflow-hidden ring-1 ring-orange-300/60 shadow-md shadow-orange-500/20">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              data-testid="muster-card-thumbnail"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-[#f97316]/25 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-[#f97316] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 text-white fill-white ml-0.5" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-[#c2410c] mb-1.5">
            Dein Muster
          </div>
          <h3
            className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-tight"
            data-testid="muster-card-headline"
          >
            Dein Einstieg in die Welt der Fokustypen
          </h3>
          <p
            className="mt-2 text-sm sm:text-base text-slate-700"
            data-testid="muster-card-subline"
          >
            Wer steuert deine Reaktionen, wenn&apos;s kippt?
          </p>

          <div
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/70 border border-orange-300 px-3 py-1.5 text-xs sm:text-sm font-medium text-[#9a3412] backdrop-blur"
            data-testid="muster-card-badge"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#f97316] animate-pulse" />
            {badgeText}
          </div>

          <div className="mt-5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              data-testid="muster-card-cta"
              className="inline-flex items-center gap-2 rounded-xl bg-[#f97316] hover:bg-[#ea6a10] px-4 py-2.5 text-sm sm:text-base font-semibold text-white shadow-md shadow-orange-500/40 transition-colors"
            >
              <Play className="w-4 h-4 fill-white" />
              Jetzt kennenlernen
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
